import { t } from '@glory/i18n';
import { Badge, Card, PageHeader } from '@glory/ui';
import { createFileRoute } from '@tanstack/react-router';

import { CHANGELOG, type ChangelogKind, type ChangelogRelease } from '../../features/news/changelog.ts';

export const Route = createFileRoute('/_app/news')({
  component: NewsPage,
});

const KINDS: readonly ChangelogKind[] = ['added', 'fixed', 'changed', 'removed'];

const LABEL: Record<ChangelogKind, () => string> = {
  added: () => t('news.added'),
  fixed: () => t('news.fixed'),
  changed: () => t('news.changed'),
  removed: () => t('news.removed'),
};

/** «Что нового»: список версий с человеческим текстом — что добавили и исправили. */
function NewsPage() {
  const current = CHANGELOG[0]?.version;
  return (
    <div className="flex min-h-full w-full flex-col gap-5 px-4 py-5 md:px-6">
      <PageHeader
        title={t('news.title')}
        actions={
          current ? (
            <Badge tone="accent">
              {t('news.version')} {current}
            </Badge>
          ) : null
        }
      />
      <p className="-mt-2 max-w-[70ch] text-muted">{t('news.lead')}</p>
      <ol className="flex flex-col gap-4">
        {CHANGELOG.map((release, index) => (
          <li key={release.version}>
            <Release release={release} current={index === 0} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function Release({ release, current }: { release: ChangelogRelease; current: boolean }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-border-soft bg-surface-2 px-4 py-3">
        <span className="font-display text-lead font-bold">{release.version}</span>
        {current ? <Badge tone="accent">{t('news.current')}</Badge> : null}
        <span className="ml-auto text-small text-muted">{release.date}</span>
      </div>
      <div className="flex flex-col gap-3.5 px-4 py-3.5">
        {KINDS.map((kind) => {
          const items = release.items.filter((item) => item.kind === kind);
          if (items.length === 0) return null;
          return (
            <section key={kind}>
              <h3 className="mb-1 text-[0.6875rem] font-extrabold tracking-[0.06em] text-muted uppercase">
                {LABEL[kind]()}
              </h3>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                {items.map((item) => (
                  <li key={item.text}>{item.text}</li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </Card>
  );
}
